/**
 * 추적 관리 한 줄이 **어디로 가나** — 줄을 누르면 가는 곳(보러 가기).
 *
 * ```
 *   우리 브랜드 키워드   → /service/tracking/brand-mentions
 *   자사 소셜 계정       → /service/operations/accounts
 *   세트                → /service/tracking/target-mentions/{id}
 *   세트의 키워드        → 〃 ?keyword=<말>(그 말을 고른 채)
 *   세트의 계정          → 〃(세트 화면 그대로)
 *   플랜 밖 구역의 줄     → 요금 화면(업그레이드 안내)
 * ```
 *
 * 🔑 고치는 일은 줄을 떠나지 않는다 — ⋯ 의 「세트 고치기」·「제외 키워드」·「스토리 연동」은 이 화면의 창이 연다.
 *   밖의 화면이 그 창으로 보낼 때는 `toStatusLinkHref`(연동) · `paths.service.tracking.at` 을 쓴다.
 * 🔑 줄을 누르는 것은 «보러 가기»다 — 막 등록한 띠(`tracked=1`)를 싣지 않는다(`toTrackLandingHref` 와 다른 길).
 * ⚠️ 순수 함수다 — `'use client'` 를 붙이지 않는다.
 */

import { paths } from 'src/routes/paths';

import { toKeywordMonitoringHref } from 'src/utils/campaign/keyword-campaign-order';


/** 줄이 가리키는 것 */
export type StatusRowTarget =
  | { kind: 'brandKeyword' }
  | { kind: 'brandAccount' }
  | { kind: 'set'; campaignId: string }
  | { kind: 'setKeyword'; campaignId: string; keyword: string }
  | { kind: 'setAccount'; campaignId: string };

/**
 * 줄을 누르면 가는 곳.
 * @returns 세트 id 가 비면 `null` — 그 줄은 누를 수 없다
 */
export function toStatusRowHref(target: StatusRowTarget): string | null {
  if (target.kind === 'brandKeyword') return paths.service.brand;
  if (target.kind === 'brandAccount') return paths.service.account;
  if (!target.campaignId) return null;
  const base = toKeywordMonitoringHref(target.campaignId);
  if (target.kind === 'setKeyword' && target.keyword.trim()) {
    return `${base}?${new URLSearchParams({ keyword: target.keyword.trim() }).toString()}`;
  }
  return base;
}

/**
 * 연동 배지·홈 「계정 연동」이 가는 곳 — 추적 관리의 자사 계정 구역(계정을 알면 그 계정의 연동 창이 열린다).
 * @param username 자사 인스타 계정 아이디 — 모르면 구역만 연다
 */
export function toStatusLinkHref(username?: string | null): string {
  return paths.service.tracking.at({ kind: 'account', linkAccount: username ?? null });
}

/** 플랜 밖 줄과 구역 머리의 「플랜 보기」가 가는 곳 — 업그레이드 안내로 연 요금 화면 */
export function toStatusUpgradeHref(): string {
  return `${paths.subscription.plan}?type=upgrade`;
}
